import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import { BsChevronDown, BsChevronRight, BsSearch } from "react-icons/bs";
import { PiListBold } from "react-icons/pi";
import { AiOutlineClose } from "react-icons/ai";
import { usePathname, useRouter } from "next/navigation";
import { HomeIcon } from "../shared/icon-svg";
import { createPortal } from "react-dom";

const useBrowserLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : () => { };

let childRefArray = [];
let itemWidthArr = [];
let maxWidth = -1;

let GLOBAL = {
    timeouts: [],
    setTimeout: function (code, number) {
        this.timeouts.push(setTimeout(code, number));
    },
    clearAllTimeout: function () {
        for (var i = 0; i < this.timeouts.length; i++) {
            window.clearTimeout(this.timeouts[i]);
        }
        this.timeouts = [];
    },
};

export default function MenuBySide() {
    const pathname = usePathname();
    const router = useRouter();
    const [categories, setCategories] = useState([]);
    const [visibleItems, setVisibleItems] = useState(0);
    const [overflowItems, setOverflowItems] = useState([]);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [hoveredMenu, setHoveredMenu] = useState(null);
    const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
    const [hoveredHamburger, setHoveredHamburger] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
    const [hamburgerDropdownPosition, setHamburgerDropdownPosition] = useState({ top: 0, left: 0 });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
    const [openMobileSubSubmenu, setOpenMobileSubSubmenu] = useState(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const menuRef = useRef();
    const hamburgerRef = useRef();
    const homeIconRef = useRef();
    const searchInputRef = useRef();

    // Fetch categories data
    const fetchData = async () => {
        try {
            const response = await fetch("/api/categories");
            const data = await response.json();
            setCategories(data);
            setVisibleItems(data.length);
        } catch (error) {
            console.error("Failed to fetch categories:", error);
        }
    };

    // Calculate visible items and overflow
    const calculateLayout = () => {
        const { current } = menuRef;
        if (!current || !categories.length) return;

        childRefArray = childRefArray.filter((c) => c);
        if (childRefArray.length !== categories.length) return;

        itemWidthArr = childRefArray.map((child) => child?.offsetWidth || 0);

        const containerWidth = current.clientWidth;
        const homeIconWidth = homeIconRef.current?.offsetWidth || 60;
        const gaps = 16; // gaps between elements

        // First, try to fit all items without hamburger
        let totalMenuWidth = itemWidthArr.reduce((sum, width) => sum + width + 8, 0) - 8; // Remove last gap
        let availableWidthWithoutHamburger = containerWidth - homeIconWidth - gaps;

        // If all items fit without hamburger, show them all
        if (totalMenuWidth <= availableWidthWithoutHamburger) {
            setIsOverflowing(false);
            setVisibleItems(categories.length);
            setOverflowItems([]);
            return;
        }

        // Otherwise, calculate with hamburger space reserved
        const hamburgerWidth = hamburgerRef.current?.offsetWidth || 80;
        const buffer = 10; // Small buffer
        const availableWidth = containerWidth - homeIconWidth - hamburgerWidth - gaps - buffer;

        let totalWidth = 0;
        let count = 0;

        for (let i = 0; i < itemWidthArr.length; i++) {
            const itemWidth = itemWidthArr[i];
            if (totalWidth + itemWidth <= availableWidth) {
                totalWidth += itemWidth;
                count++;
            } else {
                break;
            }
        }

        // Only show hamburger if we actually need it and have items to show
        const hasOverflow = count < categories.length && count > 0;
        setIsOverflowing(hasOverflow);
        setVisibleItems(hasOverflow ? count : categories.length);
        setOverflowItems(hasOverflow ? categories.slice(count) : []);
    };

    useBrowserLayoutEffect(() => {
        const { current } = menuRef;
        if (current && categories.length > 0) {
            const handleResize = () => {
                // Immediate calculation for resize
                calculateLayout();
            };

            window.addEventListener("resize", handleResize);

            // Multiple calculation attempts to ensure accuracy
            calculateLayout(); // Immediate
            setTimeout(() => calculateLayout(), 50);  // Short delay
            setTimeout(() => calculateLayout(), 200); // Medium delay
            setTimeout(() => calculateLayout(), 500); // Longer delay

            return () => window.removeEventListener("resize", handleResize);
        }
    }, [categories.length]);

    // Additional effect to recalculate when categories change
    useEffect(() => {
        if (categories.length > 0) {
            setTimeout(() => calculateLayout(), 100);
        }
    }, [categories]);

    useEffect(() => {
        fetchData();
    }, []);

    // Fallback: if visible items is 0 but we have categories, show all
    useEffect(() => {
        if (categories.length > 0 && visibleItems === 0) {
            setVisibleItems(categories.length);
            setIsOverflowing(false);
            setOverflowItems([]);
        }
    }, [categories.length, visibleItems]);

    useEffect(() => {
        if (pathname === "/") {
            setHoveredMenu(null);
            setHoveredSubmenu(null);
            setHoveredHamburger(false);
        }
        // Close mobile menu and search on route change
        setIsMobileMenuOpen(false);
        setOpenMobileSubmenu(null);
        setOpenMobileSubSubmenu(null);
        setIsSearchOpen(false);
        setSearchQuery("");
    }, [pathname]);

    // Lock body scroll when mobile menu or search is open
    useEffect(() => {
        if (isMobileMenuOpen || isSearchOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen, isSearchOpen]);

    // Focus search input when search opens
    useEffect(() => {
        if (isSearchOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isSearchOpen]);

    // Handle Escape key to close search modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isSearchOpen) {
                setIsSearchOpen(false);
                setSearchQuery("");
            }
        };

        if (isSearchOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isSearchOpen]);

    // Handle search submission
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setIsSearchOpen(false);
            setSearchQuery("");
        }
    };

    // Handle mouse leave with delay
    const handleMouseLeave = (callback) => {
        GLOBAL.setTimeout(() => {
            callback();
        }, 300);
    };

    // Handle mouse enter - clear timeouts
    const handleMouseEnter = () => {
        GLOBAL.clearAllTimeout();
    };

    // Calculate dropdown position relative to viewport
    const calculateDropdownPosition = (element, isInHamburger = false) => {
        if (!element) return { top: 0, left: 0 };

        const rect = element.getBoundingClientRect();
        if (isInHamburger) {
            return {
                top: rect.top,
                left: rect.right + 4
            };
        } else {
            return {
                top: rect.bottom + 4,
                left: rect.left
            };
        }
    };

    // Calculate submenu position relative to viewport
    const calculateSubmenuPosition = (element, isInHamburger = false) => {
        if (!element) return { top: 0, left: 0 };

        const rect = element.getBoundingClientRect();
        if (isInHamburger) {
            // Hamburger menu: show level 3 submenu on the LEFT
            return {
                top: rect.top,
                left: rect.left - 200 - 4
            };
        } else {
            // Main menu: show level 3 submenu on the RIGHT
            return {
                top: rect.top,
                left: rect.right + 4
            };
        }
    };

    // Render menu item
    const renderMenuItem = (item, index, isInHamburger = false) => {
        const isActive = hoveredMenu === item.code;
        const hasChildren = item.data && item.data.length > 0;

        if (item.type === "link") {
            return (
                <div
                    key={item.code}
                    ref={!isInHamburger ? (r) => { childRefArray[index] = r; } : null}
                    className={`relative ${!isInHamburger ? 'w-fit' : 'w-full'}`}
                >
                    <Link
                        href={item.code}
                        className={`
                            block px-4 py-3 font-bold text-sm transition-colors duration-200 whitespace-nowrap
                            ${isInHamburger
                                ? 'text-white bg-primary hover:bg-secondary hover:text-white w-full'
                                : 'text-white bg-primary hover:bg-secondary hover:text-white'
                            }
                        `}
                    >
                        {item.name}
                    </Link>
                </div>
            );
        }

        return (
            <div
                key={item.code}
                ref={!isInHamburger ? (r) => { childRefArray[index] = r; } : null}
                className={`relative ${!isInHamburger ? 'w-fit' : 'w-full'}`}
                onMouseEnter={(e) => {
                    handleMouseEnter();
                    if (isInHamburger) {
                        // Show dropdown on hover in hamburger menu
                        setHoveredMenu(item.code);
                    } else {
                        // Clear hamburger dropdown when hovering main menu
                        setHoveredHamburger(false);
                        setHoveredMenu(item.code);
                        const position = calculateDropdownPosition(e.currentTarget, isInHamburger);
                        setDropdownPosition(position);
                    }
                }}
                onMouseLeave={() => {
                    if (isInHamburger) {
                        handleMouseLeave(() => setHoveredMenu(null));
                    } else {
                        handleMouseLeave(() => {
                            setHoveredMenu(null);
                            setHoveredSubmenu(null);
                        });
                    }
                }}
            >
                <Link
                    href={`/chuyen-muc/${item.code}`}
                    className={`
                        flex items-center gap-2 px-4 py-3 font-bold text-sm transition-colors duration-200 whitespace-nowrap
                        ${isInHamburger
                            ? 'text-white bg-primary hover:bg-secondary hover:text-primary w-full justify-start'
                            : 'text-white bg-primary hover:bg-secondary hover:text-primary'
                        }
                        ${isActive ? 'bg-secondary !text-primary' : ''}
                    `}
                >
                    {item.name}
                    {hasChildren && (
                        <BsChevronDown className={`transition-transform duration-200 ${isActive && isInHamburger ? 'rotate-180' : ''}`} />
                    )}
                </Link>

                {/* Dropdown menu - using portal for non-hamburger items */}
                {hasChildren && isActive && !isInHamburger && typeof window !== 'undefined' && createPortal(
                    <div
                        className="min-w-[200px] bg-white border border-gray-200 shadow-lg animate-slide-in-down"
                        style={{
                            position: 'fixed',
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            zIndex: 99999
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={() => {
                            handleMouseLeave(() => {
                                setHoveredMenu(null);
                                setHoveredSubmenu(null);
                            });
                        }}
                    >
                        {item.data.map((subItem) => renderSubmenuItem(subItem, isInHamburger))}
                    </div>,
                    document.body
                )}

                {/* Dropdown menu - inline for hamburger items - show on LEFT */}
                {hasChildren && isActive && isInHamburger && (
                    <div
                        className="absolute right-full top-0 mr-1 min-w-[200px] bg-white border border-gray-200 shadow-lg animate-slide-in-left"
                        style={{
                            zIndex: 99999,
                            position: 'absolute'
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={() => {
                            if (isInHamburger) {
                                handleMouseLeave(() => setHoveredMenu(null));
                            }
                        }}
                    >
                        {item.data.map((subItem) => renderSubmenuItem(subItem, isInHamburger))}
                    </div>
                )}
            </div>
        );
    };

    // Render submenu item
    const renderSubmenuItem = (subItem, isInHamburger = false) => {
        const hasChildren = subItem.data && subItem.data.length > 0;
        const isActive = hoveredSubmenu === subItem.code;

        return (
            <div
                key={subItem.code}
                className="relative"
                onMouseEnter={(e) => {
                    handleMouseEnter();
                    setHoveredSubmenu(subItem.code);
                    if (hasChildren && !isInHamburger) {
                        const position = calculateSubmenuPosition(e.currentTarget, isInHamburger);
                        setSubmenuPosition(position);
                    }
                }}
                onMouseLeave={() => {
                    handleMouseLeave(() => setHoveredSubmenu(null));
                }}
            >
                {hasChildren ? (
                    <button
                        className={`
                            flex items-center justify-between px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap w-full text-left
                            hover:bg-secondary text-black
                            ${isActive ? 'bg-secondary text-white' : ''}
                        `}
                    >
                        <span>{subItem.name}</span>
                        <BsChevronRight className="ml-2" />
                    </button>
                ) : (
                    <Link
                        href={subItem.type === "categories" ? `/chuyen-de/${subItem.code}` : subItem.code}
                        className={`
                            flex items-center justify-between px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap
                            hover:bg-secondary text-black
                        `}
                    >
                        <span>{subItem.name}</span>
                    </Link>
                )}

                {/* Sub-submenu - using portal for non-hamburger items - showing on RIGHT */}
                {hasChildren && isActive && !isInHamburger && typeof window !== 'undefined' && createPortal(
                    <div
                        className="min-w-[200px] bg-white border border-gray-200 shadow-lg animate-slide-in-right"
                        style={{
                            position: 'fixed',
                            top: submenuPosition.top,
                            left: submenuPosition.left,
                            zIndex: 99999
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={() => {
                            handleMouseLeave(() => setHoveredSubmenu(null));
                        }}
                    >
                        {subItem.data.map((childItem) => (
                            <Link
                                key={childItem.code}
                                href={childItem.type === "categories" ? `/chuyen-de/${childItem.code}` : childItem.code}
                                className="block px-4 py-3 text-sm text-black hover:bg-primary hover:text-white transition-colors duration-200 whitespace-nowrap"
                            >
                                {childItem.name}
                            </Link>
                        ))}
                    </div>,
                    document.body
                )}

                {/* Sub-submenu - inline for hamburger items - show further LEFT */}
                {hasChildren && isActive && isInHamburger && (
                    <div
                        className="absolute right-full top-0 mr-1 min-w-[200px] bg-white border border-gray-200 shadow-lg animate-slide-in-left"
                        style={{
                            zIndex: 99999,
                            position: 'absolute'
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={() => {
                            handleMouseLeave(() => setHoveredSubmenu(null));
                        }}
                    >
                        {subItem.data.map((childItem) => (
                            <Link
                                key={childItem.code}
                                href={childItem.type === "categories" ? `/chuyen-de/${childItem.code}` : childItem.code}
                                className="block px-4 py-3 text-sm text-black hover:bg-primary hover:text-white transition-colors duration-200 whitespace-nowrap"
                            >
                                {childItem.name}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Render mobile menu item
    const renderMobileMenuItem = (item) => {
        const isOpen = openMobileSubmenu === item.code;
        const hasChildren = item.data && item.data.length > 0;

        if (item.type === "link") {
            return (
                <div key={item.code} className="border-b border-gray-100">
                    <Link
                        href={item.code}
                        className="block px-5 py-4 text-[15px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 transition-colors uppercase tracking-wide"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        {item.name}
                    </Link>
                </div>
            );
        }

        return (
            <div key={item.code} className="border-b border-gray-100">
                {hasChildren ? (
                    <>
                        <div className="w-full flex items-center justify-between group">
                            <Link
                                href={`/chuyen-muc/${item.code}`}
                                className="flex-1 px-5 py-4 text-[15px] font-medium text-gray-800 uppercase tracking-wide hover:bg-gray-50 active:bg-gray-100 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {item.name}
                            </Link>
                            <div className="px-4 py-4 flex items-center justify-center">
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setOpenMobileSubmenu(isOpen ? null : item.code);
                                    }}
                                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-primary hover:text-white hover:shadow-md active:shadow-lg transition-all duration-200"
                                    aria-label="Toggle submenu"
                                >
                                    <BsChevronDown
                                        className={`transition-transform duration-300 text-sm ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                            </div>
                        </div>
                        {isOpen && (
                            <div className="bg-gray-50">
                                {item.data.map((subItem) => renderMobileSubMenuItem(subItem))}
                            </div>
                        )}
                    </>
                ) : (
                    <Link
                        href={`/chuyen-muc/${item.code}`}
                        className="block px-5 py-4 text-[15px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 transition-colors uppercase tracking-wide"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        {item.name}
                    </Link>
                )}
            </div>
        );
    };

    // Render mobile submenu item
    const renderMobileSubMenuItem = (subItem) => {
        const isOpen = openMobileSubSubmenu === subItem.code;
        const hasChildren = subItem.data && subItem.data.length > 0;

        return (
            <div key={subItem.code} className="border-b border-gray-100 last:border-b-0">
                {hasChildren ? (
                    <>
                        <div className="w-full flex items-center justify-between group">
                            <Link
                                href={subItem.type === "categories" ? `/chuyen-de/${subItem.code}` : subItem.code}
                                className="flex-1 px-8 py-3.5 text-sm text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {subItem.name}
                            </Link>
                            <div className="px-4 py-3.5 flex items-center justify-center">
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setOpenMobileSubSubmenu(isOpen ? null : subItem.code);
                                    }}
                                    className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-primary hover:text-white hover:shadow-md active:shadow-lg transition-all duration-200"
                                    aria-label="Toggle submenu"
                                >
                                    <BsChevronDown
                                        className={`transition-transform duration-300 text-xs ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                            </div>
                        </div>
                        {isOpen && (
                            <div className="bg-white">
                                {subItem.data.map((childItem) => (
                                    <Link
                                        key={childItem.code}
                                        href={childItem.type === "categories" ? `/chuyen-de/${childItem.code}` : childItem.code}
                                        className="block px-12 py-3 text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors border-b border-gray-50 last:border-b-0"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        {childItem.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <Link
                        href={subItem.type === "categories" ? `/chuyen-de/${subItem.code}` : subItem.code}
                        className="block px-8 py-3.5 text-sm text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        {subItem.name}
                    </Link>
                )}
            </div>
        );
    };

    return (
        <div className="w-full bg-primary relative">
            <div className="xl:w-[1286px] mx-auto px-3 md:px-[90px]">
                <nav
                    ref={menuRef}
                    className="flex items-center gap-4 relative"
                >
                    {/* Home Icon */}
                    <div
                        ref={homeIconRef}
                        className="flex-shrink-0"
                    >
                        <Link
                            href="/"
                            className="block p-2 text-white hover:bg-secondary hover:text-white transition-colors duration-200 rounded"
                            title="Trang chủ"
                        >
                            <HomeIcon />
                        </Link>
                    </div>

                    {/* Desktop Menu container */}
                    <div className="hidden md:flex items-center justify-between w-full">
                        {/* Visible menu items */}
                        <div className="flex items-center gap-2 flex-1 overflow-hidden relative">
                            {categories.slice(0, visibleItems).map((item, index) =>
                                renderMenuItem(item, index)
                            )}
                        </div>

                        {/* Desktop Search Button */}
                        {/* <button
                            onClick={() => setIsSearchOpen(!isSearchOpen)}
                            className="flex items-center gap-2 px-3 py-3 font-bold text-sm transition-colors duration-200 text-white bg-primary hover:bg-secondary hover:text-white rounded ml-2"
                            aria-label="Search"
                        >
                            <BsSearch className="text-lg" />
                        </button> */}

                        {/* Hamburger menu for overflow items */}
                        {isOverflowing && overflowItems.length > 0 && (
                            <div
                                ref={hamburgerRef}
                                className="relative flex-shrink-0 ml-1"
                                onMouseEnter={(e) => {
                                    handleMouseEnter();
                                    // Clear main menu dropdowns when hovering hamburger
                                    setHoveredMenu(null);
                                    setHoveredSubmenu(null);
                                    setHoveredHamburger(true);
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHamburgerDropdownPosition({
                                        top: rect.bottom + 4,
                                        left: rect.right - 200
                                    });
                                }}
                                onMouseLeave={() => {
                                    handleMouseLeave(() => {
                                        setHoveredHamburger(false);
                                        setHoveredMenu(null);
                                        setHoveredSubmenu(null);
                                    });
                                }}
                            >
                                <button
                                    className={`
                                        flex items-center gap-2 px-4 py-3 font-bold text-sm transition-colors duration-200 whitespace-nowrap
                                        text-white bg-primary hover:bg-secondary hover:text-white
                                        ${hoveredHamburger ? 'bg-secondary text-white' : ''}
                                    `}
                                >
                                    <PiListBold className="text-xl" />
                                </button>

                                {/* Hamburger dropdown - using portal */}
                                {hoveredHamburger && typeof window !== 'undefined' && createPortal(
                                    <div
                                        className="min-w-[200px] bg-white border border-gray-200 shadow-lg animate-slide-in-down"
                                        style={{
                                            position: 'fixed',
                                            top: hamburgerDropdownPosition.top,
                                            left: hamburgerDropdownPosition.left,
                                            zIndex: 99999
                                        }}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={() => {
                                            handleMouseLeave(() => {
                                                setHoveredHamburger(false);
                                                setHoveredMenu(null);
                                                setHoveredSubmenu(null);
                                            });
                                        }}
                                    >
                                        {overflowItems.map((item, index) => (
                                            <div key={item.code} className="relative">
                                                {renderMenuItem(item, visibleItems + index, true)}
                                            </div>
                                        ))}
                                    </div>,
                                    document.body
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile Search & Menu Toggle Buttons */}
                    <div className="md:hidden flex-1 flex justify-end items-center gap-2">
                        <button
                            onClick={() => setIsSearchOpen(!isSearchOpen)}
                            className="flex items-center gap-2 px-3 py-3 font-bold text-sm transition-colors duration-200 text-white bg-primary hover:bg-secondary hover:text-white rounded"
                            aria-label="Toggle search"
                        >
                            <BsSearch className="text-lg" />
                        </button>
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="flex items-center gap-2 px-3 py-3 font-bold text-sm transition-colors duration-200 text-white bg-primary hover:bg-secondary hover:text-white rounded"
                            aria-label="Toggle mobile menu"
                        >
                            <PiListBold className="text-xl" />
                        </button>
                    </div>
                </nav>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && typeof window !== 'undefined' && createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/50 z-[100000] animate-fade-in md:hidden"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />

                    {/* Menu Panel */}
                    <div
                        className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-[100001] shadow-2xl overflow-y-auto animate-slide-in-right-full md:hidden"
                    >
                        {/* Menu Header */}
                        <div className="flex items-center justify-between px-5 py-4 bg-primary sticky top-0 z-10">
                            <h2 className="text-lg font-semibold text-white tracking-wide">Danh Mục</h2>
                            <button
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="p-1.5 text-white hover:bg-white/20 rounded-lg transition-colors"
                                aria-label="Close menu"
                            >
                                <AiOutlineClose className="text-2xl" />
                            </button>
                        </div>

                        {/* Menu Items */}
                        <div className="bg-white">
                            {categories.map((item) => renderMobileMenuItem(item))}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* Search Modal */}
            {isSearchOpen && typeof window !== 'undefined' && createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 z-[100002] animate-fade-in"
                        onClick={() => setIsSearchOpen(false)}
                    />

                    {/* Desktop Search Modal */}
                    <div className="hidden md:block fixed inset-x-0 top-0 z-[100003] animate-slide-down">
                        <div className="max-w-2xl mx-auto mt-24 px-4">
                            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                                {/* Search Header */}
                                <div className="flex items-center gap-4 px-6 py-5">
                                    <BsSearch className="text-2xl text-gray-400 flex-shrink-0" />
                                    <form onSubmit={handleSearch} className="flex-1">
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Tìm kiếm bài viết, tin tức..."
                                            className="w-full text-lg outline-none text-gray-900 placeholder-gray-400"
                                        />
                                    </form>
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery("")}
                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                            aria-label="Clear search"
                                        >
                                            <AiOutlineClose className="text-lg" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setIsSearchOpen(false);
                                            setSearchQuery("");
                                        }}
                                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                        aria-label="Close search"
                                    >
                                        <AiOutlineClose className="text-xl" />
                                    </button>
                                </div>

                                {/* Search Results Preview or Quick Actions */}
                                {searchQuery && (
                                    <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
                                        <button
                                            onClick={handleSearch}
                                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-white transition-colors text-left"
                                        >
                                            <BsSearch className="text-gray-400" />
                                            <span className="text-gray-700">Tìm kiếm &quot;{searchQuery}&quot;</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Search Modal */}
                    <div className="md:hidden fixed inset-0 z-[100003] bg-white animate-slide-down">
                        <div className="h-full flex flex-col">
                            {/* Mobile Search Header with sticky position */}
                            <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
                                <button
                                    onClick={() => {
                                        setIsSearchOpen(false);
                                        setSearchQuery("");
                                    }}
                                    className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                                    aria-label="Back"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>

                                <form onSubmit={handleSearch} className="flex-1 flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
                                    <BsSearch className="text-lg text-gray-400 flex-shrink-0" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Tìm kiếm..."
                                        className="w-full text-base outline-none bg-transparent text-gray-900 placeholder-gray-400"
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery("")}
                                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                            aria-label="Clear search"
                                        >
                                            <AiOutlineClose className="text-lg" />
                                        </button>
                                    )}
                                </form>
                            </div>

                            {/* Search Content Area */}
                            <div className="flex-1 overflow-y-auto bg-white">
                                {!searchQuery ? (
                                    /* Empty state - show helpful content */
                                    <div className="px-4 py-8">
                                        <div className="text-center mb-8">
                                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                                <BsSearch className="text-3xl text-gray-400" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Tìm kiếm nội dung</h3>
                                            <p className="text-sm text-gray-500">Tìm bài viết, tin tức và nhiều hơn nữa</p>
                                        </div>

                                        {/* Popular searches or categories */}
                                        <div className="space-y-4">
                                            <h4 className="text-sm font-semibold text-gray-700 px-2">Danh mục phổ biến</h4>
                                            <div className="space-y-1">
                                                {categories.slice(0, 6).map((cat) => (
                                                    <Link
                                                        key={cat.code}
                                                        href={`/chuyen-muc/${cat.code}`}
                                                        onClick={() => {
                                                            setIsSearchOpen(false);
                                                            setSearchQuery("");
                                                        }}
                                                        className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
                                                    >
                                                        <BsSearch className="text-gray-400 text-sm" />
                                                        <span className="text-gray-700 text-sm">{cat.name}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Show search in progress state */
                                    <div className="px-4 py-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <p className="text-sm text-gray-500">
                                                Nhấn tìm kiếm để xem kết quả
                                            </p>
                                            <button
                                                onClick={handleSearch}
                                                className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-secondary transition-colors"
                                            >
                                                Tìm kiếm
                                            </button>
                                        </div>

                                        {/* Quick action */}
                                        <div
                                            onClick={handleSearch}
                                            className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                                        >
                                            <BsSearch className="text-gray-400" />
                                            <span className="text-gray-700">Tìm kiếm &quot;{searchQuery}&quot;</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>,
                document.body
            )}

            <style jsx>{`
                @keyframes slideInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes slideInRightFull {
                    from {
                        transform: translateX(100%);
                    }
                    to {
                        transform: translateX(0);
                    }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-slide-in-down {
                    animation: slideInDown 0.2s ease-out forwards;
                }

                .animate-slide-in-right {
                    animation: slideInRight 0.2s ease-out forwards;
                }

                .animate-slide-in-left {
                    animation: slideInLeft 0.2s ease-out forwards;
                }

                .animate-slide-in-right-full {
                    animation: slideInRightFull 0.3s ease-out forwards;
                }

                .animate-fade-in {
                    animation: fadeIn 0.2s ease-out forwards;
                }

                .animate-slide-down {
                    animation: slideDown 0.3s ease-out forwards;
                }

                /* Allow horizontal overflow control while letting dropdowns escape vertically */
                nav {
                    overflow: visible !important;
                }
                
                /* Keep horizontal menu items contained but allow vertical dropdowns */
                nav > div {
                    overflow-x: hidden;
                    overflow-y: visible;
                }
                
                /* Ensure container doesn't clip vertically */
                :global(.container) {
                    overflow-x: hidden;
                    overflow-y: visible;
                }

                /* Ensure dropdowns can escape their containers */
                .relative {
                    overflow: visible;
                }
            `}</style>
        </div>
    );
}